import React, { FormEvent, useMemo, useState, ReactNode } from "react";
import LoggedOutPage from "./LoggedOutPage";
import Countdown from "./Countdown";
import Loading from "./Loading";
import { Link, useSearchParams } from "react-router-dom";
import { IfComp } from "@dvrt/react-tools";
import { findErrors } from "../utils/passwordTools";
import { FormattedMessage } from "react-intl";
import { useUserData } from "../context/UserDataProvider";
import { useFetch } from "./FetchProvider";
import {
    Box,
    Button,
    FormControlLabel,
    Switch,
    Stack,
    TextField,
    Typography,
    Alert,
    Dialog,
    DialogContent,
} from "@mui/material";
import PasswordField from "./PasswordField";

const rememberStorageKey = "remember-checkbox-value";

type LoginSuccess = {
    result:
        | "success"
        | "totp_code_needed"
        | "invalid_totp_code"
        | "password_change_needed"
        | "missing_client_certificate";
    redirect_to?: string; /// only set in case of success
};

type PasswordChangeNeededResponse = {
    result: "password_change_needed";
    mandatory: boolean;
};

type LoginFailure = {
    result: "invalid_credentials" | "too_many_login_attempts" | "current_password_mismatch";
    timeout?: number;
};

type PasswordChangeFailure = {
    result: "current_password_mismatch" | "invalid_password_strength";
} & PasswordStrengthRequirementResponse;

type Stage =
    | { stage: "start" }
    | { stage: "totp"; invalid_code: boolean }
    | { stage: "password_change"; mandatory: boolean };

type PasswordChangeData =
    | {
          currentPassword: string;
          newPassword: string;
      }
    | "postpone";

type PasswordStrengthRequirementField = "min_len" | "min_lowercase" | "min_uppercase" | "min_digits" | "min_special";

type PasswordStrengthRequirementResponse = {
    [key in PasswordStrengthRequirementField]?: number;
};
type LoginFormData = {
    username: string;
    password: string;
    remember: boolean;
};

const LoginForm = ({
    onSubmit,
    state,
    setState,
    serverError,
}: {
    onSubmit: () => void;
    state: LoginFormData;
    setState: (values: Partial<LoginFormData>) => void;
    serverError: string | null;
}) => {
    return (
        <Box
            component="form"
            noValidate
            onSubmit={(event: FormEvent) => {
                event.preventDefault();
                event.stopPropagation();
                localStorage.setItem(rememberStorageKey, JSON.stringify(state.remember));
                onSubmit();
            }}
            sx={{
                minWidth: "25ch", // limit form width to 25 characters
                maxWidth: "30ch", // ensure texts wrap for better layout
            }}
        >
            <Stack spacing={2}>
                <TextField
                    label={<FormattedMessage id="login.username" />}
                    variant="outlined"
                    type="text"
                    value={state.username}
                    onChange={(ev) => setState({ username: ev.target.value })}
                    required
                    fullWidth
                    autoFocus={true}
                    autoComplete="username"
                    name="username"
                />

                <PasswordField
                    label="login.password"
                    value={state.password}
                    onChange={(ev) => setState({ password: ev.target.value })}
                    autoComplete="current-password"
                    name="password"
                />

                {serverError && (
                    <Alert severity="error">
                        <FormattedMessage id={serverError} />
                    </Alert>
                )}

                <IfComp condition={window.features.rememberMe}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={state.remember}
                                onChange={(ev) => setState({ remember: ev.target.checked })}
                            />
                        }
                        label={<FormattedMessage id="login.remember" />}
                        labelPlacement="start"
                    />
                </IfComp>

                <Button type="submit" variant="contained" color="primary">
                    <FormattedMessage id="submit" />
                </Button>

                <Link to="/public/request-password-reset">
                    <FormattedMessage id="reset_password.login_form_link" />
                </Link>
            </Stack>
        </Box>
    );
};

type PasswordChangeFormState = {
    currentPassword: string;
    newPassword: string;
    retypePassword: string;
};

function errorList(errors: { id: string; values: any }[]) {
    return (
        <ul>
            {errors.map((error) => (
                <li>
                    <FormattedMessage id={error.id} values={error.values} />
                </li>
            ))}
        </ul>
    );
}

const PasswordChangeForm = ({
    onSubmit,
    mandatory,
    currentPasswordMismatch,
    serverError,
}: {
    onSubmit: (data: PasswordChangeData) => Promise<void>;
    mandatory: boolean;
    currentPasswordMismatch: boolean;
    serverError: string | null;
}) => {
    const [state, _setState] = useState<PasswordChangeFormState>({
        currentPassword: "",
        newPassword: "",
        retypePassword: "",
    });

    const setState = (values: Partial<PasswordChangeFormState>) => {
        _setState((prev) => ({ ...prev, ...values }));
    };

    const newPasswordValidationErrors = useMemo(() => {
        const strengthConfig = window.passwordStrength;
        if (strengthConfig === undefined) return [];

        return findErrors(strengthConfig, state.newPassword);
    }, [state.newPassword, window.passwordStrength]);

    return (
        <Box
            component="form"
            noValidate
            onSubmit={(event: FormEvent) => {
                event.preventDefault();
                event.stopPropagation();
                onSubmit({ currentPassword: state.currentPassword, newPassword: state.newPassword });
            }}
            sx={{
                width: "30ch",
            }}
        >
            <Stack spacing={2}>
                <PasswordField
                    label="change_password.current_password"
                    value={state.currentPassword}
                    onChange={(ev) => setState({ currentPassword: ev.target.value })}
                    autoComplete="current-password"
                    error={currentPasswordMismatch}
                    helperText={
                        currentPasswordMismatch ? <FormattedMessage id="current_password_does_not_match" /> : ""
                    }
                    name="current_password"
                />

                <PasswordField
                    label="change_password.new_password"
                    value={state.newPassword}
                    onChange={(ev) => setState({ newPassword: ev.target.value })}
                    autoComplete="new-password"
                    error={state.newPassword && newPasswordValidationErrors.length > 0}
                    helperText={
                        state.newPassword && newPasswordValidationErrors.length
                            ? errorList(newPasswordValidationErrors)
                            : null
                    }
                    name="new_password"
                />

                <PasswordField
                    label="change_password.retype_password"
                    value={state.retypePassword}
                    onChange={(ev) => setState({ retypePassword: ev.target.value })}
                    autoComplete="new-password"
                    error={state.newPassword != state.retypePassword}
                    helperText={state.newPassword != state.retypePassword ? "NA" : null}
                    name="retype_password"
                />

                {serverError && (
                    <Alert severity="error">
                        <FormattedMessage id={serverError} />
                    </Alert>
                )}

                <Stack direction="row" spacing={1} justifyContent="stretch" sx={{ width: "100%" }}>
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={
                            state.currentPassword.length === 0 ||
                            state.newPassword.length === 0 ||
                            newPasswordValidationErrors.length > 0 ||
                            state.newPassword !== state.retypePassword
                        }
                        sx={{ flex: 1 }}
                    >
                        <FormattedMessage id="submit" />
                    </Button>
                    <IfComp condition={!mandatory}>
                        <Button
                            name="postpone"
                            type="button" // if type is omitted it is treated as 'submit'
                            variant="contained"
                            color="secondary"
                            onClick={() => onSubmit("postpone")}
                            sx={{ flex: 1 }}
                        >
                            <FormattedMessage id="postpone" />
                        </Button>
                    </IfComp>
                </Stack>
            </Stack>
        </Box>
    );
};

const TOTPForm = ({
    onSubmit,
    hasError,
    serverError,
}: {
    hasError: boolean;
    onSubmit: (code: string) => void;
    serverError: string | null;
}) => {
    const [totpCode, setTotpCode] = useState<string>("");

    return (
        <Box
            component="form"
            noValidate
            onSubmit={(event: FormEvent) => {
                event.preventDefault();
                event.stopPropagation();
                onSubmit(totpCode);
            }}
            sx={{
                minWidth: "25ch", // minimum width to 25 characters,
                maxWidth: "30ch", // ensure texts wrap for better layout
            }}
        >
            <Stack spacing={2}>
                {serverError && (
                    <Alert severity="error">
                        <FormattedMessage id={serverError} />
                    </Alert>
                )}
                <Typography variant="body2" color="text.secondary">
                    <FormattedMessage id="account.totp.verify.description" />
                </Typography>

                <TextField
                    label={<FormattedMessage id="account.totp.verify.title" />}
                    variant="outlined"
                    type="text"
                    value={totpCode}
                    onChange={(ev) => setTotpCode(ev.target.value)}
                    required
                    autoFocus={true}
                    slotProps={{
                        input: {
                            inputProps: {
                                maxLength: 6,
                                inputMode: "numeric",
                                pattern: "[0-9]*",
                            },
                        },
                    }}
                    autoComplete="one-time-code"
                    error={hasError}
                    helperText={
                        hasError ? (
                            <FormattedMessage id="account.totp.generate_stage.code_verification.error.invalid_code" />
                        ) : (
                            ""
                        )
                    }
                />

                <Button disabled={totpCode.length !== 6} variant="contained" color="primary" type="submit">
                    <FormattedMessage id="submit" />
                </Button>
            </Stack>
        </Box>
    );
};

/**
 * Login component
 */
const Login = () => {
    const [serverError, setServerError] = useState<string | null>(null);
    const [tooManyAttemptsTimeout, setTooManyAttemptsTimeout] = useState<number>(null);
    const [displayLoader, setDisplayLoader] = useState<boolean>(false);
    const [stage, setStage] = useState<Stage>({ stage: "start" });
    const userData = useUserData();
    const fetch = useFetch();
    const [searchParams] = useSearchParams();
    const flowId = searchParams.get("flow");

    // We have to declare it here, otherwise if we try to catch the error in the child component,
    // since we display a loader instead of the change password form component, React will throw a
    // 'Can't perform a React state update on an unmounted component' error.
    // So we need to pass the error through the parent component.
    const [passwordChangeCurrentMismatch, setPasswordChangeCurrentMismatch] = useState<boolean>(false);

    // State is stored here, because due to conditional rendering, even if we
    // hold the LoginForm in a useMemo or useCallback, the component would
    // unmount, causing the useStates to be reset inside the LoginForm.
    const [loginFormState, setLoginFormState] = useState<LoginFormData>({
        username: "",
        password: "",
        remember: window.features.rememberMe && JSON.parse(localStorage.getItem(rememberStorageKey) || "false"),
    });

    function loginSuccess(response: LoginSuccess) {
        console.debug("Login success");
        window.location.href = response.redirect_to;
    }

    async function _onSubmit() {
        console.debug("onSubmit");
        setDisplayLoader(true);
        setServerError(null);
        const resp = await fetch("/openid-provider/authorization-api", {
            method: "POST",
            body: JSON.stringify({
                username: loginFormState.username,
                password: loginFormState.password,
                remember: loginFormState.remember,
                flow: flowId,
            }),
            headers: {
                "Content-Type": "application/json",
            },
        });
        if (resp.ok) {
            const response: LoginSuccess = await resp.json();
            switch (response.result) {
                case "success":
                    loginSuccess(response);
                    return;
                case "totp_code_needed":
                    setStage({ stage: "totp", invalid_code: false });
                    break;
                case "password_change_needed":
                    const mandatory = (response as PasswordChangeNeededResponse).mandatory;
                    setStage({ stage: "password_change", mandatory });
                    break;
                case "missing_client_certificate":
                    window.location.href = response.redirect_to;
                    break;
                default:
                    setServerError("login.unexpected_error");
            }
        } else {
            const response: LoginFailure = await resp.json();

            const setTooManyAttemptsTimeoutAndReset = (timeout: number) => {
                setTooManyAttemptsTimeout(timeout);
                setTimeout(() => {
                    setTooManyAttemptsTimeout(null);
                }, timeout * 1000);
            };

            switch (response.result) {
                case "invalid_credentials":
                    setServerError("login.invalid_credentials");
                    if (response.timeout) {
                        setTooManyAttemptsTimeoutAndReset(response.timeout);
                    }
                    break;
                case "too_many_login_attempts":
                    setServerError("login.too_many_attempts_notification");
                    setTooManyAttemptsTimeoutAndReset(response.timeout);
                    break;
                default:
                    setServerError("login.unexpected_error");
            }
        }
        setDisplayLoader(false);
    }

    async function _onTotpSubmit(code: string) {
        console.debug("onTotpSubmit");
        setDisplayLoader(true);
        setServerError(null);
        const resp = await fetch("/openid-provider/verify-totp-code", {
            method: "POST",
            body: JSON.stringify({ code, flow: flowId }),
            headers: {
                "Content-Type": "application/json",
            },
        });
        if (resp.ok) {
            const response: LoginSuccess = await resp.json();
            switch (response.result) {
                case "success":
                    loginSuccess(response);
                    return;
                case "invalid_totp_code":
                    setStage({ stage: "totp", invalid_code: true });
                    break;
                case "password_change_needed":
                    const mandatory = (response as PasswordChangeNeededResponse).mandatory;
                    setStage({ stage: "password_change", mandatory });
                    break;
            }
        } else setServerError("login.unexpected_error");
        setDisplayLoader(false);
    }

    async function _submitPasswordChange(data: PasswordChangeData) {
        setPasswordChangeCurrentMismatch(false);
        let body: Record<string, any>;
        if (data === "postpone") {
            console.debug("postponing password change");
            body = { postpone: true };
        } else {
            console.debug("submit password change");
            body = {
                current_password: data.currentPassword,
                new_password: data.newPassword,
            };
        }
        body["flow"] = flowId;

        setDisplayLoader(true);
        setServerError(null);
        const resp = await fetch("/openid-provider/change-password", {
            method: "POST",
            body: JSON.stringify(body),
            headers: {
                "Content-Type": "application/json",
            },
        });
        if (resp.ok) {
            const response: LoginSuccess = await resp.json();
            if (response.result == "success") {
                loginSuccess(response);
                return;
            } else setServerError("login.unexpected_error");
        } else {
            const response: PasswordChangeFailure = await resp.json();

            if (response.result === "current_password_mismatch") {
                setPasswordChangeCurrentMismatch(true);
            } else setServerError("login.unexpected_error");
        }
        setDisplayLoader(false);
    }

    if (userData) {
        return (
            <LoggedOutPage title={<FormattedMessage id="login.page_title" />}>
                <Stack>
                    <Box sx={{ paddingRight: 5 }}>
                        <FormattedMessage id="login.already_logged_in" />
                    </Box>
                    <a href="/">
                        <FormattedMessage id="login.go_home" />
                    </a>
                </Stack>
            </LoggedOutPage>
        );
    }

    let content: ReactNode;

    if (stage.stage === "totp") {
        content = <TOTPForm hasError={stage.invalid_code} onSubmit={_onTotpSubmit} serverError={serverError} />;
    } else if (stage.stage === "password_change") {
        // stage is password change
        content = (
            <PasswordChangeForm
                onSubmit={_submitPasswordChange}
                mandatory={stage.mandatory}
                currentPasswordMismatch={passwordChangeCurrentMismatch}
                serverError={serverError}
            />
        );
    } else {
        content = (
            <LoginForm
                onSubmit={_onSubmit}
                state={loginFormState}
                setState={(values) => setLoginFormState((prev) => ({ ...prev, ...values }))}
                serverError={serverError}
            />
        );
    }

    return (
        <LoggedOutPage title={<FormattedMessage id="login.page_title" />}>
            {content}

            <Dialog open={tooManyAttemptsTimeout > 0}>
                <DialogContent sx={{ textAlign: "center" }}>
                    <Alert severity="warning">
                        <FormattedMessage id="too_many_attempts_message" />
                    </Alert>
                    <Countdown time={tooManyAttemptsTimeout} />
                </DialogContent>
            </Dialog>

            <Dialog open={displayLoader}>
                <DialogContent>
                    <Loading />
                </DialogContent>
            </Dialog>
        </LoggedOutPage>
    );
};

export default Login;
